import type { CSSProperties } from 'react';
import { markUserEdit } from '../hooks/useUnsavedGuard';
import './SpinInput.css';

/** White triangle on the themed spin button (an SVG, so it shows whatever icon font or text colour rule is in force). */
function SpinTri({ up }: { up?: boolean }) {
  return (
    <svg width="8" height="5" viewBox="0 0 8 5" aria-hidden="true" style={{ display: 'block' }}>
      <path d={up ? 'M0 5L4 0L8 5Z' : 'M0 0L4 5L8 0Z'} fill="#fff" />
    </svg>
  );
}

const SEP = '1px solid var(--brand-accent-hover, var(--ai-orange-hover))';

/**
 * The site's number stepper (GoLive spin editor / XAF SpinEdit): a numeric box with a stacked ▲ / ▼ pair in the active
 * accent colour, ALWAYS visible (a bare `<input type="number">` only shows Chrome's grey spinner on hover, which no theme
 * can recolour). Same look as the Probability field of the Clients prospect popup; the value is clamped to [min, max] and
 * an empty box stays empty. Pass `width` / `height` to size it inside a dialog's own grid.
 *
 * ONE shared stepper (standing rule 8) — used by the Pre-Login recurrence dialog, the Clients Probability field and the
 * Reagent Prep No Of Levels. Opt-ins added 2026-11-04 when the Reagent Prep copy was merged into this one:
 *   · `className` / `id` — a host's own field look (the detail form's yellow 24px box);
 *   · a ▲ / ▼ click marks the field as edited, so the shared "Unsaved changes" guard sees it (standing rule 15).
 */
export function SpinInput({ value, onChange, min = 0, max = 100, step = 1, disabled, width, height, ariaLabel, className, id }: {
  value: string;
  onChange: (v: string) => void;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  width?: number | string;
  height?: number | string;
  ariaLabel?: string;
  /** OPT-IN: extra class on the box, for a host's own field look. */
  className?: string;
  id?: string;
}) {
  const clamp = (n: number) => String(Math.min(max, Math.max(min, n)));
  const bump = (d: number, el: HTMLElement) => {
    markUserEdit(el);   // a button press is a real edit — the unsaved guard must see it
    onChange(clamp((Number(value) || 0) + d * step));
  };
  const spinBtn: CSSProperties = {
    border: 'none', background: 'var(--brand-accent, var(--ai-orange))', cursor: disabled ? 'default' : 'pointer',
    width: 18, flex: 1, minHeight: 10, fontSize: 8, lineHeight: '10px', padding: 0, color: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  };
  return (
    <div className={`spin-input${className ? ` ${className}` : ''}`} style={{ width, height, opacity: disabled ? 0.55 : 1 }}>
      <input id={id} type="number" min={min} max={max} step={step} value={value} disabled={disabled} aria-label={ariaLabel}
             onChange={e => onChange(e.target.value === '' ? '' : clamp(Number(e.target.value)))} />
      <span className="spin-input-btns" style={{ borderLeft: SEP }}>
        <button type="button" tabIndex={-1} title="Increase" aria-label="Increase" disabled={disabled}
                onMouseDown={e => e.preventDefault()} onClick={e => bump(1, e.currentTarget)} style={spinBtn}><SpinTri up /></button>
        <button type="button" tabIndex={-1} title="Decrease" aria-label="Decrease" disabled={disabled}
                onMouseDown={e => e.preventDefault()} onClick={e => bump(-1, e.currentTarget)} style={{ ...spinBtn, borderTop: SEP }}><SpinTri /></button>
      </span>
    </div>
  );
}
